import type {
	SupportTicketComment,
	SupportTicketStatus,
	SupportTicketType,
} from "@pilatestime-admin/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, MessageSquare, Send, Ticket, User } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	fetchSupportTicketAdminQueryOptions,
	useAddSupportTicketCommentAdmin,
} from "@/api/supportTicketsAdmin";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useApiParameters } from "@/hooks/useApiParameters";

export const Route = createFileRoute(
	"/_auth/app/_app/support-tickets/$ticketId/",
)({
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);

		context.queryClient.ensureQueryData(
			fetchSupportTicketAdminQueryOptions(token, params.ticketId),
		);

		return { userId, token, ticketId: params.ticketId };
	},
	component: SupportTicketDetailPage,
});

function SupportTicketDetailPage() {
	const { t } = useTranslation();
	const { token, ticketId } = Route.useLoaderData();
	const commentInputId = useId();

	const { data: ticket } = useSuspenseQuery(
		fetchSupportTicketAdminQueryOptions(token, ticketId),
	);

	const [commentContent, setCommentContent] = useState("");
	const addCommentMutation = useAddSupportTicketCommentAdmin();

	const handleCommentSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!commentContent.trim()) return;

		try {
			await addCommentMutation.mutateAsync({
				token,
				ticketId,
				data: { content: commentContent.trim() },
			});
			setCommentContent("");
		} catch (error) {
			console.error("Error adding comment:", error);
		}
	};

	const getStatusBadge = (status: SupportTicketStatus) => {
		const statusClasses = {
			open: "badge-warning",
			waiting_for_user: "badge-info",
			waiting_for_staff: "badge-primary",
			resolved: "badge-success",
			closed: "badge-ghost",
		};
		return (
			<span className={`badge badge-sm ${statusClasses[status]}`}>
				{t(`supportTickets.statuses.${status}`)}
			</span>
		);
	};

	const getTypeBadge = (type: SupportTicketType) => {
		const typeClasses = {
			complaint: "badge-error",
			request: "badge-info",
			suggestion: "badge-success",
		};
		return (
			<span className={`badge badge-sm ${typeClasses[type]}`}>
				{t(`supportTickets.types.${type}`)}
			</span>
		);
	};

	const formatDate = (dateString: string) => {
		const date = new Date(dateString);
		return date.toLocaleString();
	};

	const getCommentAuthorName = (comment: SupportTicketComment): string => {
		if (comment.isStaff) {
			return (
				comment.staffName || comment.staffEmail || t("supportTickets.staffTeam")
			);
		}
		// User comment
		return comment.user
			? `${comment.user.firstName || ""} ${comment.user.lastName || ""}`.trim() ||
					comment.user.email
			: t("supportTickets.user");
	};

	const renderComment = (comment: SupportTicketComment) => {
		const authorName = getCommentAuthorName(comment);
		const isStaffComment = comment.isStaff;

		return (
			<div
				key={comment.id}
				className={`flex space-x-3 p-4 border-b border-base-200 last:border-b-0 ${
					isStaffComment ? "bg-primary/5" : ""
				}`}
			>
				<div className="shrink-0">
					<div
						className={`w-8 h-8 rounded-full flex items-center justify-center ${
							isStaffComment ? "bg-primary/20" : "bg-base-300"
						}`}
					>
						<User
							className={`w-4 h-4 ${
								isStaffComment ? "text-primary" : "text-base-content/60"
							}`}
						/>
					</div>
				</div>
				<div className="flex-1 min-w-0">
					<div className="flex items-center space-x-2 mb-1">
						<span className="text-sm font-medium text-base-content">
							{authorName}
						</span>
						{isStaffComment && (
							<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary">
								{t("supportTickets.staff")}
							</span>
						)}
						<span className="text-xs text-base-content/60">
							{formatDate(comment.createdAt)}
						</span>
					</div>
					<div className="text-sm text-base-content whitespace-pre-wrap">
						{comment.content}
					</div>
				</div>
			</div>
		);
	};

	return (
		<div className="container mx-auto p-6 space-y-6 max-w-4xl">
			<div className="flex items-center gap-4">
				<Link to="/app/support-tickets" className="btn btn-ghost btn-sm">
					<ArrowLeft className="h-4 w-4" />
					{t("common.back")}
				</Link>
				<div className="flex-1">
					<h1 className="text-2xl font-bold text-base-content">
						{ticket.title}
					</h1>
					<p className="text-sm text-base-content/60">
						{t("supportTickets.ticketId")}: {ticket.id.slice(-8)}
					</p>
				</div>
				<div className="flex items-center gap-2">
					{getTypeBadge(ticket.type)}
					{getStatusBadge(ticket.status)}
				</div>
			</div>

			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<Ticket className="h-5 w-5" />
						<span>{t("supportTickets.ticketInformation")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div>
							<Label className="text-sm font-medium text-base-content/70">
								{t("supportTickets.table.user")}
							</Label>
							<div className="mt-1">
								<div className="font-medium">
									{`${ticket.user.firstName || ""} ${ticket.user.lastName || ""}`.trim() ||
										ticket.user.email}
								</div>
								<div className="text-xs text-base-content/60">
									{ticket.user.email}
								</div>
							</div>
						</div>
						<div>
							<Label className="text-sm font-medium text-base-content/70">
								{t("supportTickets.table.createdAt")}
							</Label>
							<div className="mt-1">{formatDate(ticket.createdAt)}</div>
						</div>
					</div>
					<div>
						<Label className="text-sm font-medium text-base-content/70">
							{t("supportTickets.description")}
						</Label>
						<div className="mt-1 p-3 bg-base-200 rounded-lg">
							<p className="text-sm whitespace-pre-wrap">
								{ticket.description}
							</p>
						</div>
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<MessageSquare className="h-5 w-5" />
						<span>
							{t("supportTickets.comments")} ({ticket.comments.length})
						</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="p-0">
					{/* Comments List */}
					{ticket.comments.length === 0 ? (
						<div className="p-6 text-center">
							<MessageSquare className="h-12 w-12 text-base-content/30 mx-auto mb-4" />
							<p className="text-base-content/70">
								{t("supportTickets.noComments")}
							</p>
						</div>
					) : (
						<div className="max-h-96 overflow-y-auto">
							{ticket.comments.map((comment) => renderComment(comment))}
						</div>
					)}

					{/* Add Comment Form */}
					<div className="border-t border-base-200 p-4">
						<form onSubmit={handleCommentSubmit} className="space-y-4">
							<div>
								<Label htmlFor={commentInputId} className="text-sm font-medium">
									{t("supportTickets.addComment")}
								</Label>
								<Textarea
									id={commentInputId}
									value={commentContent}
									onChange={(e) => setCommentContent(e.target.value)}
									placeholder={t("supportTickets.commentPlaceholder")}
									rows={3}
									className="mt-1"
									disabled={addCommentMutation.isPending}
									maxLength={2000}
								/>
								<div className="flex justify-between items-center mt-2">
									<span className="text-xs text-base-content/50">
										{commentContent.length}/2000
									</span>
								</div>
							</div>
							<div className="flex justify-end">
								<Button
									type="submit"
									disabled={
										!commentContent.trim() || addCommentMutation.isPending
									}
									className="flex items-center space-x-2 text-primary-content"
								>
									{addCommentMutation.isPending ? (
										<>
											<div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
											<span>{t("supportTickets.commentSubmitting")}</span>
										</>
									) : (
										<>
											<Send className="h-4 w-4" />
											<span>{t("supportTickets.sendComment")}</span>
										</>
									)}
								</Button>
							</div>
						</form>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
